import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import 'react-select/dist/react-select.css';
import styles from './MultiSelectCheckboxDropdown.scss';

export const OptionItemContainer = ({
  isSelected,
  children,
  innerProps,
  onChangeHandler,
  option,
}) => {
  const [isChecked, setIsChecked] = useState(isSelected);
  const handleOption = () => {
    setIsChecked(!isChecked);
    onChangeHandler(option);
  };
  return (
    <div
      {...innerProps}
      className={styles.optionContainer}
    >
      <input
        type="checkbox"
        checked={isChecked}
        onChange={handleOption}
        className={styles.optionCheckBox}
      />
      <div
        className={styles.optionLabel}
        onClick={handleOption}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleOption();
          }
        }}
        tabIndex="0"
        role="button"
        data-testid="optionContainerTestId"
      >{children}
      </div>
    </div>
  );
};

const RenderValueComponent = ({ value }) => (
  value.label ? <span className={styles.valueContainer} data-testid="valueContainerTestId">{value.label}</span> : null
);

const transformOptions = options => options.map(option => ({
  value: option,
  label: option,
  clearableValue: false,
}));

const transformDisplayOptions = (selectedLabels) => {
  let transformedLabels = selectedLabels;

  if (!selectedLabels) {
    return [];
  }

  if (selectedLabels.length > 2) {
    transformedLabels = selectedLabels.map((item, index) => {
      if (index < 2) {
        return {
          ...item,
          label: `${item.value.trim().replace(/,/g, '')},`,
        };
      }

      return {
        ...item,
        label:
          index === selectedLabels.length - 1
            ? `+ ${selectedLabels.length - 2} more`
            : undefined,
      };
    });
  } else if (selectedLabels.length === 2) {
    transformedLabels = selectedLabels.map((item, index) => ({
      ...item,
      label: index === 0 ? `${item.value},` : item.value,
    }));
  }

  return transformedLabels;
};

const selectControlStyle = {
  width: '315px',
  border: '1px solid #686868',
};

const menuContainerStyle = {
  display: 'flex',
  width: '315px',
  padding: '16px',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '24px',
  border: '1px solid #EBEBEB',
  background: '#FFF',
  boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.15)',
};

const MultiSelectCheckboxDropdown = ({ options, onSelect, shouldResetStateFilter }) => {
  const transformedOptions = transformOptions(options);
  const reverseTransformOptions = selOptions => selOptions.map(option => option.value);
  const [selectedOptions, setSelectedOptions] = useState([]);

  const resetStatesFilter = () => {
    setSelectedOptions([]);
  };

  useEffect(() => {
    if (shouldResetStateFilter) {
      resetStatesFilter();
    }
  }, [shouldResetStateFilter]);

  const handleChange = (selected) => {
    /* selected is an array of deselected options when the user clicks on the x button.
    Othercases it is an object */
    let updatedOptions;
    if (Array.isArray(selected) && selected.length === 0) {
      updatedOptions = [];
      setSelectedOptions(selected);
    } else {
      const optionExists = selectedOptions.some(
        option => option.value === selected.value,
      );
      if (optionExists) {
        // remove
        updatedOptions = selectedOptions.filter(
          option => option.value !== selected.value,
        );
        setSelectedOptions(updatedOptions);
      } else {
        // add
        updatedOptions = [...selectedOptions, selected];
        setSelectedOptions(updatedOptions);
      }
    }
    onSelect(reverseTransformOptions(updatedOptions));
    return updatedOptions;
  };

  return (
    <div id="Select-multi-dropdown">
      <Select
        multi
        className={'Select-multi-dropdown'}
        style={selectControlStyle}
        value={transformDisplayOptions(selectedOptions)}
        closeOnSelect={false}
        removeSelected={false}
        onChange={handleChange}
        options={transformedOptions}
        backspaceRemoves
        placeholder={'Filter by State'}
        menuStyle={{}}
        menuContainerStyle={menuContainerStyle}
        valueComponent={props => (
          <RenderValueComponent {...props} />
        )}
        optionComponent={props => (
          <OptionItemContainer
            {...props}
            selectedOptions={selectedOptions}
            onChangeHandler={handleChange}
          />
        )}
        onInputKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
          }
        }}
      />
    </div>
  );
};

export default MultiSelectCheckboxDropdown;
